import React, { useEffect, useMemo, useState } from 'react';
import {
  CircleDashed, CircleHalf, CheckCircle, ClipboardText, Star,
  Wrench, Sunglasses, Sparkle, ShieldCheck,
} from '@phosphor-icons/react';
import { api } from '../api';
import { ROLE_STATUS_COL, ROLE_CHECKLIST_TYPE, statusLabel } from '../helpers';
import { BrandTag, LocationBadge, StatusBadge } from '../components/Badges';
import { rowClass, DateCell } from '../components/VehicleRow';

const STATUS_FILTERS = ['All', 'Not Started', 'In Progress', 'Complete'];
const PIPE_ICONS = { workshop: Wrench, tint: Sunglasses, detail: Sparkle, paint: ShieldCheck };
const STATUS_TO_VAL = { 'Not Started': 'ns', 'In Progress': 'prog', 'Complete': 'done' };

export default function Queue({ session, refreshKey, onUpdate, onOpenChecklist }) {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('All');
  const [search, setSearch]     = useState('');

  const myCol     = ROLE_STATUS_COL[session.role] || 'status_detail';
  const checklist = ROLE_CHECKLIST_TYPE[session.role] || 'detail';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.listVehicles()
      .then((v) => { if (!cancelled) setVehicles(v); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  // Show vehicles where my sector is relevant (status not 'na')
  const relevant = useMemo(() => vehicles.filter((v) => v[myCol] !== 'na'), [vehicles, myCol]);

  const counts = useMemo(() => ({
    ns:    relevant.filter((v) => v[myCol] === 'ns').length,
    prog:  relevant.filter((v) => v[myCol] === 'prog').length,
    done:  relevant.filter((v) => v[myCol] === 'done').length,
    total: relevant.length,
  }), [relevant, myCol]);

  const filtered = useMemo(() => {
    let list = relevant;
    if (filter !== 'All') list = list.filter((v) => v[myCol] === STATUS_TO_VAL[filter]);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((v) =>
        (v.stock_number || '').toLowerCase().includes(q) ||
        (v.model || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [relevant, filter, search, myCol]);

  if (loading) return <div className="loading">Loading queue…</div>;

  return (
    <div>
      <div className="ph">
        <div>
          <div className="pt">{session.role === 'Mechanic' ? 'Workshop' : session.role} Queue</div>
          <div className="ps">Your assigned vehicles — your status is highlighted · complete with checklist</div>
        </div>
      </div>

      <div className="stats">
        <StatCard icon={CircleDashed}  cls="r"  value={counts.ns}    label="Not Started" />
        <StatCard icon={CircleHalf}    cls="am" value={counts.prog}  label="In Progress" />
        <StatCard icon={CheckCircle}   cls="gr" value={counts.done}  label="Complete" />
        <StatCard icon={ClipboardText} cls="bl" value={counts.total} label="Total" />
      </div>

      <div className="fbar">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f}
            className={`fb ${filter === f ? 'on' : ''}`}
            onClick={() => setFilter(f)}
          >{f}</button>
        ))}
        <input
          className="fsrch"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="tw">
        <table className="vt">
          <thead>
            <tr>
              <th>Vehicle</th><th>Brand</th><th>Location</th><th>Delivery</th>
              <th style={{ background: 'var(--accent-bg)' }}>
                <Star size={11} weight="fill" style={{ verticalAlign: '-1px', marginRight: 4, color: 'var(--accent)' }} />My Status
              </th>
              <th>Full Pipeline</th><th>Notes</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr className="empty"><td colSpan={8}>No vehicles in your queue.</td></tr>
            ) : filtered.map((v) => (
              <tr key={v.id} className={rowClass(v, vehicles)}>
                <td>
                  <div className="vm">{v.model}</div>
                  <div className="vs">{v.stock_number}</div>
                </td>
                <td><BrandTag brand={v.brand} /></td>
                <td><LocationBadge location={v.location} /></td>
                <td><DateCell vehicle={v} /></td>
                <td style={{ background: 'var(--accent-bg)' }}><StatusBadge value={v[myCol]} /></td>
                <td>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {['workshop', 'tint', 'detail', 'paint']
                      .filter((k) => v[`status_${k}`] !== 'na')
                      .map((k) => (
                        <span key={k} className={`sb ${v[`status_${k}`]}`} style={{ fontSize: 9 }}>
                          {React.createElement(PIPE_ICONS[k], { size: 11, weight: 'duotone' })} {statusLabel(v[`status_${k}`])}
                        </span>
                      ))}
                  </div>
                </td>
                <td style={{ maxWidth: 130, fontSize: 11, color: 'var(--muted)' }}>{v.notes}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {v[myCol] !== 'done' && (
                    <button
                      className="ab g"
                      style={{ marginRight: 4 }}
                      onClick={() => onOpenChecklist(checklist, v)}
                    ><ClipboardText size={12} weight="duotone" style={{ verticalAlign: '-2px', marginRight: 3 }} />Checklist</button>
                  )}
                  <button className="ab p" onClick={() => onUpdate(v.id)}>Update</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, cls, value, label }) {
  return (
    <div className="sc">
      <div className={`sc-icon ${cls}`}><Icon size={20} weight="duotone" /></div>
      <div className="sc-data">
        <div className="sn">{value}</div>
        <div className="sl">{label}</div>
      </div>
    </div>
  );
}
